import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Package, Upload, X, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  ["planned", "Planned"], ["waiting_parts", "Waiting on Parts"], ["parts_received", "Parts Received"],
  ["scheduled", "Scheduled"], ["in_progress", "In Progress"], ["completed", "Completed"],
];
const PRIORITIES = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["critical", "Critical"]];

const field = "w-full px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none";
const lbl = "text-white/50 text-xs font-semibold uppercase tracking-wide mb-1 block";

const blankPart = () => ({ id: crypto.randomUUID(), name: "", ordered: false, order_date: "", vendor: "", tracking_number: "", cost: 0, received: false, installed: false });

export default function WorkOrderDialog({ token, workOrder, vehicles, services, defaultVehicleId, onClose, onSaved }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const editing = !!workOrder;
  const [f, setF] = useState({
    vehicle_id: defaultVehicleId || "", title: "", service_type: "Oil Change", status: "planned",
    priority: "medium", odometer: 0, service_date: new Date().toISOString().slice(0, 10),
    estimated_cost: 0, actual_cost: 0, vendor: "", notes: "", interval_miles: 0, interval_months: 0,
    parts: [], receipts: [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (workOrder) {
      setF({
        vehicle_id: workOrder.vehicle_id || "", title: workOrder.title || "", service_type: workOrder.service_type || "Oil Change",
        status: workOrder.status || "planned", priority: workOrder.priority || "medium", odometer: workOrder.odometer || 0,
        service_date: (workOrder.service_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
        estimated_cost: workOrder.estimated_cost || 0, actual_cost: workOrder.actual_cost || 0, vendor: workOrder.vendor || "",
        notes: workOrder.notes || "", interval_miles: workOrder.interval_miles || 0, interval_months: workOrder.interval_months || 0,
        parts: (workOrder.parts || []).map((p) => ({ ...p })), receipts: (workOrder.receipts || []).map((r) => ({ ...r })),
      });
    }
  }, [workOrder]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onServiceChange = (name) => {
    const svc = services.find((s) => s.name === name);
    setF((s) => ({
      ...s, service_type: name,
      title: s.title || name,
      interval_miles: svc && !editing ? svc.default_interval_miles : s.interval_miles,
      interval_months: svc && !editing ? svc.default_interval_months : s.interval_months,
    }));
  };

  const addPart = () => set("parts", [...f.parts, blankPart()]);
  const updatePart = (id, k, v) => set("parts", f.parts.map((p) => (p.id === id ? { ...p, [k]: v } : p)));
  const removePart = (id) => set("parts", f.parts.filter((p) => p.id !== id));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload?public=true`, fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
        setF((s) => ({ ...s, receipts: [...s.receipts, { url: data.url, path: data.path, filename: file.name, content_type: file.type }] }));
      }
      toast.success("Receipt uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!f.vehicle_id) { toast.error("Select a vehicle"); return; }
    setSaving(true);
    const payload = {
      ...f, odometer: Number(f.odometer) || 0, estimated_cost: Number(f.estimated_cost) || 0,
      actual_cost: Number(f.actual_cost) || 0, interval_miles: Number(f.interval_miles) || 0,
      interval_months: Number(f.interval_months) || 0,
      parts: f.parts.map((p) => ({ ...p, cost: Number(p.cost) || 0 })),
    };
    try {
      if (editing) await axios.patch(`${API}/work-orders/${workOrder.id}`, payload, auth);
      else await axios.post(`${API}/work-orders`, payload, auth);
      toast.success(editing ? "Work order updated" : "Work order created");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const partsCost = f.parts.reduce((a, p) => a + (Number(p.cost) || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="work-order-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Work Order" : "New Work Order"}</DialogTitle>
          <DialogDescription className="text-white/50">Track the repair, its parts, costs and scheduling for this vehicle.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Vehicle</label>
            <select data-testid="wo-vehicle-select" className={field} value={f.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)}>
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{`${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim()}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Service Type</label>
            <select data-testid="wo-service-select" className={field} value={f.service_type} onChange={(e) => onServiceChange(e.target.value)}>
              {services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Title / Description</label>
            <input data-testid="wo-title-input" className={field} value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Front Suspension Rebuild" />
          </div>
          <div>
            <label className={lbl}>Status</label>
            <select data-testid="wo-status-select" className={field} value={f.status} onChange={(e) => set("status", e.target.value)}>
              {STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Priority</label>
            <select data-testid="wo-priority-select" className={field} value={f.priority} onChange={(e) => set("priority", e.target.value)}>
              {PRIORITIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Current Odometer</label>
            <input type="number" data-testid="wo-odometer-input" className={field} value={f.odometer} onChange={(e) => set("odometer", e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Service Date</label>
            <input type="date" data-testid="wo-date-input" className={field} value={f.service_date} onChange={(e) => set("service_date", e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Estimated Cost ($)</label>
            <input type="number" className={field} value={f.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Actual Cost ($)</label>
            <input type="number" data-testid="wo-actual-cost-input" className={field} value={f.actual_cost} onChange={(e) => set("actual_cost", e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Vendor / Shop</label>
            <input className={field} value={f.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="Repair shop" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lbl}>Remind every (mi)</label>
              <input type="number" data-testid="wo-interval-miles" className={field} value={f.interval_miles} onChange={(e) => set("interval_miles", e.target.value)} placeholder="5000" />
            </div>
            <div>
              <label className={lbl}>Remind every (mo)</label>
              <input type="number" data-testid="wo-interval-months" className={field} value={f.interval_months} onChange={(e) => set("interval_months", e.target.value)} placeholder="6" />
            </div>
          </div>
        </div>

        <div>
          <label className={lbl}>Notes</label>
          <textarea rows={2} className={field} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>

        {/* Parts */}
        <div className="rounded-lg border border-white/10 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-white font-semibold text-sm"><Package size={15} className="text-[#F2B705]" /> Parts ({f.parts.length}) · ${partsCost.toFixed(2)}</div>
            <button data-testid="wo-add-part-btn" onClick={addPart} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-[#F2B705] text-black font-semibold"><Plus size={13} /> Add Part</button>
          </div>
          {f.parts.length === 0 ? <p className="text-white/30 text-xs">No parts yet. Add the parts this repair needs.</p> : (
            <div className="space-y-2" data-testid="wo-parts-list">
              {f.parts.map((p) => (
                <div key={p.id} className="rounded-md bg-black/50 border border-white/10 p-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
                    <input className={field + " sm:col-span-2"} placeholder="Part name" value={p.name} onChange={(e) => updatePart(p.id, "name", e.target.value)} />
                    <input className={field} placeholder="Vendor" value={p.vendor} onChange={(e) => updatePart(p.id, "vendor", e.target.value)} />
                    <input type="number" className={field} placeholder="Cost" value={p.cost} onChange={(e) => updatePart(p.id, "cost", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                    <input className={field} placeholder="Tracking #" value={p.tracking_number} onChange={(e) => updatePart(p.id, "tracking_number", e.target.value)} />
                    <input type="date" className={field} value={p.order_date} onChange={(e) => updatePart(p.id, "order_date", e.target.value)} />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {[["ordered", "Ordered"], ["received", "Received"], ["installed", "Installed"]].map(([k, label]) => (
                      <button key={k} onClick={() => updatePart(p.id, k, !p[k])}
                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded border ${p[k] ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-white/50 border-white/10"}`}>
                        {p[k] ? <Check size={12} /> : null} {label}
                      </button>
                    ))}
                    <button onClick={() => removePart(p.id)} className="ml-auto text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Receipts */}
        <div className="rounded-lg border border-white/10 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-white font-semibold text-sm">Receipts / Photos ({f.receipts.length})</div>
            <label className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-[#0C0C0C] border border-white/15 text-white/80 cursor-pointer">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
              <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="wo-receipt-upload" />
            </label>
          </div>
          {f.receipts.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {f.receipts.map((r, i) => (
                <div key={i} className="relative">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${r.url}`} alt="receipt" className="w-16 h-16 object-cover rounded border border-white/10" />
                  <button onClick={() => set("receipts", f.receipts.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5"><X size={11} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-[#0C0C0C] border border-white/10 text-white/70 text-sm font-semibold">Cancel</button>
          <button data-testid="wo-save-btn" onClick={save} disabled={saving}
            className="px-5 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 size={15} className="animate-spin" />} {editing ? "Save Changes" : "Create Work Order"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
